import React from 'react';
import { isInFrame } from './embedResize';
import { useAuthStore } from '../store/auth';

/**
 * Which part of this document's viewport the person can actually see.
 *
 * An auto-height frame is as tall as its content (often thousands of pixels), so
 * anything positioned against the frame viewport — dialog centring, snackbars,
 * "is this field on screen?" checks — works against the whole tall frame instead
 * of the slice of it that is inside the host's window.
 *
 * Without host cooperation, an IntersectionObserver with the implicit root (the
 * top-level viewport, which works across origins) reports each target's visible
 * rectangle in this document's viewport coordinates. A single full-frame target
 * is not enough: its ratio stays constant while the host scrolls through the
 * middle of a tall frame, so no callback fires. Contiguous horizontal stripes
 * change ratio at the band edges and keep the band current.
 *
 * A host can also push `responsedock.command.viewport { visibleTop, visibleHeight }`
 * (px from the frame's top edge); once received it overrides the observer.
 */

export type FrameViewportSource = 'window' | 'observer' | 'host' | 'unknown';

export interface FrameViewport {
  framed: boolean;
  /** Top of the visible band in this document's viewport coordinates (px from the frame's top edge). */
  top: number;
  /** Height of the visible band. */
  height: number;
  /** Height of this document's own viewport (the frame height when framed). */
  viewportHeight: number;
  source: FrameViewportSource;
}

export interface HostViewportCommand {
  visibleTop: number;
  visibleHeight: number;
}

export interface BandRect {
  top: number;
  bottom: number;
}

export const VIEWPORT_COMMAND_TYPE = 'responsedock.command.viewport';
/** The leading part of a target that must be visible for it to count as on screen. */
export const MIN_VISIBLE_PX = 48;
/** Dialogs never get a band shorter than this (or the frame, if shorter). */
export const MIN_DIALOG_BAND_PX = 320;
export const STRIPE_THRESHOLDS: number[] = Array.from({ length: 51 }, (_, index) => index / 50);
const MIN_STRIPE_PX = 80;
const MAX_STRIPES = 150;
const MAX_PX = 1_000_000;

/** Validates a host-pushed viewport command. Returns null for anything else. */
export function parseViewportCommand(data: unknown): HostViewportCommand | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as Record<string, unknown>;
  if (value.type !== VIEWPORT_COMMAND_TYPE) return null;
  if (value.protocol !== undefined && value.protocol !== 'responsedock.embed') return null;
  if (value.version !== undefined && value.version !== 1) return null;
  const { visibleTop, visibleHeight } = value;
  if (typeof visibleTop !== 'number' || typeof visibleHeight !== 'number') return null;
  if (!Number.isFinite(visibleTop) || !Number.isFinite(visibleHeight)) return null;
  if (visibleHeight < 0 || Math.abs(visibleTop) > MAX_PX || visibleHeight > MAX_PX) return null;
  return { visibleTop, visibleHeight };
}

/** Union of the visible parts of contiguous stripes; null when nothing is visible. */
export function unionBand(rects: Iterable<BandRect | null>): { top: number; height: number } | null {
  let top = Number.POSITIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const rect of rects) {
    if (!rect || rect.bottom <= rect.top) continue;
    top = Math.min(top, rect.top);
    bottom = Math.max(bottom, rect.bottom);
  }
  return Number.isFinite(top) ? { top, height: bottom - top } : null;
}

/** Stripe size and count covering a viewport; edge resolution is about 2% of a stripe. */
export function stripePlan(viewportHeight: number): { size: number; count: number } {
  const height = Math.max(1, Math.ceil(viewportHeight));
  const size = Math.max(MIN_STRIPE_PX, Math.ceil(height / MAX_STRIPES));
  return { size, count: Math.ceil(height / size) };
}

function clampBand(top: number, height: number, viewportHeight: number): { top: number; height: number } {
  const clampedTop = Math.min(Math.max(0, top), viewportHeight);
  const bottom = Math.min(Math.max(clampedTop, top + height), viewportHeight);
  return { top: clampedTop, height: bottom - clampedTop };
}

/**
 * The band dialogs are placed in: the visible band, grown around its centre to a
 * usable minimum (a dialog squeezed into a 40px sliver helps nobody), within the frame.
 */
export function dialogBand(viewport: FrameViewport, minHeight = MIN_DIALOG_BAND_PX): { top: number; height: number; bottom: number } {
  const frameHeight = Math.max(0, viewport.viewportHeight);
  const wanted = Math.min(frameHeight, Math.max(viewport.height, minHeight));
  const centre = viewport.top + viewport.height / 2;
  const top = Math.min(Math.max(0, centre - wanted / 2), Math.max(0, frameHeight - wanted));
  return { top, height: wanted, bottom: Math.max(0, frameHeight - top - wanted) };
}

/** True when the leading `minVisible` px of [top, top + height] lie inside the visible band. */
export function isRangeVisible(top: number, height: number, viewport: FrameViewport, minVisible = MIN_VISIBLE_PX): boolean {
  if (viewport.source === 'unknown') return false;
  const needed = Math.min(Math.max(0, height), minVisible);
  return top >= viewport.top - 1 && top + needed <= viewport.top + viewport.height + 1;
}

// ---------------------------------------------------------------------------
// Store

const listeners = new Set<() => void>();
let subscribers = 0;
let stopTracking: (() => void) | null = null;
let observedBand: { top: number; height: number } | null = null;
let hostBand: { top: number; height: number } | null = null;
let hostSeen = false;
let snapshot: FrameViewport = computeViewport();

function ownViewportHeight(): number {
  return window.innerHeight || document.documentElement.clientHeight || 0;
}

function computeViewport(): FrameViewport {
  const viewportHeight = ownViewportHeight();
  if (!isInFrame()) return { framed: false, top: 0, height: viewportHeight, viewportHeight, source: 'window' };
  const band = hostBand ?? observedBand;
  if (!band) return { framed: true, top: 0, height: viewportHeight, viewportHeight, source: 'unknown' };
  return { framed: true, ...clampBand(band.top, band.height, viewportHeight), viewportHeight, source: hostBand ? 'host' : 'observer' };
}

function sameViewport(a: FrameViewport, b: FrameViewport): boolean {
  return a.framed === b.framed
    && a.source === b.source
    && Math.round(a.top) === Math.round(b.top)
    && Math.round(a.height) === Math.round(b.height)
    && Math.round(a.viewportHeight) === Math.round(b.viewportHeight);
}

function refresh(): void {
  const next = computeViewport();
  if (sameViewport(next, snapshot)) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

function trustedHostOrigin(origin: string): boolean {
  const allowed = useAuthStore.getState().embedContext?.allowedOrigin;
  if (!allowed) return false;
  try {
    return new URL(allowed).origin === origin;
  } catch {
    return false;
  }
}

function observeStripes(): () => void {
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.setAttribute('data-rd-viewport-probe', '');
  container.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;overflow:hidden;pointer-events:none;visibility:hidden;z-index:-1';
  // Outside <body>, so the content-size observers never see the probe.
  document.documentElement.appendChild(container);
  const rects = new Map<Element, BandRect | null>();
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!rects.has(entry.target)) continue;
      const rect = entry.intersectionRect;
      rects.set(entry.target, entry.isIntersecting && rect.height > 0 ? { top: rect.top, bottom: rect.bottom } : null);
    }
    observedBand = unionBand(rects.values()) ?? { top: 0, height: 0 };
    refresh();
  }, { threshold: STRIPE_THRESHOLDS, rootMargin: '0px' });

  let plannedHeight = -1;
  const layout = () => {
    const height = Math.ceil(ownViewportHeight());
    if (height === plannedHeight) return;
    plannedHeight = height;
    const { size, count } = stripePlan(height);
    observer.disconnect();
    rects.clear();
    container.replaceChildren();
    for (let index = 0; index < count; index += 1) {
      const stripe = document.createElement('div');
      stripe.style.cssText = `position:absolute;left:0;right:0;top:${index * size}px;height:${size}px`;
      container.appendChild(stripe);
      rects.set(stripe, null);
      observer.observe(stripe);
    }
  };
  layout();
  let frame = 0;
  const onResize = () => {
    window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(layout);
  };
  window.addEventListener('resize', onResize);
  return () => {
    window.removeEventListener('resize', onResize);
    window.cancelAnimationFrame(frame);
    observer.disconnect();
    container.remove();
  };
}

function startTracking(): () => void {
  const cleanups: Array<() => void> = [];
  const onResize = () => refresh();
  window.addEventListener('resize', onResize);
  cleanups.push(() => window.removeEventListener('resize', onResize));
  if (isInFrame()) {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const command = parseViewportCommand(event.data);
      if (!command || !trustedHostOrigin(event.origin)) return;
      hostSeen = true;
      hostBand = { top: command.visibleTop, height: command.visibleHeight };
      refresh();
    };
    window.addEventListener('message', onMessage);
    cleanups.push(() => window.removeEventListener('message', onMessage));
    if (typeof IntersectionObserver === 'function') cleanups.push(observeStripes());
  }
  return () => cleanups.forEach((cleanup) => cleanup());
}

export function subscribeFrameViewport(listener: () => void): () => void {
  listeners.add(listener);
  subscribers += 1;
  if (subscribers === 1) stopTracking = startTracking();
  refresh();
  return () => {
    listeners.delete(listener);
    subscribers -= 1;
    if (subscribers === 0) {
      stopTracking?.();
      stopTracking = null;
      observedBand = null;
    }
  };
}

/** Current visible band. Accurate while something subscribes (see FrameViewportSync). */
export function getFrameViewport(): FrameViewport {
  if (subscribers === 0) {
    const next = computeViewport();
    if (!sameViewport(next, snapshot)) snapshot = next;
  }
  return snapshot;
}

// Read once: the app rewrites its URL after the embed token exchange.
const hostScrollHint = (() => {
  try {
    return new URLSearchParams(window.location.search).get('rd_scroll') === 'host';
  } catch {
    return false;
  }
})();

/**
 * True when the host is known to act on `responsedock.lifecycle.scroll_request`:
 * the frame URL carried `rd_scroll=host`, or the host pushed a viewport command.
 */
export function hostHandlesScrollRequests(): boolean {
  return hostSeen || hostScrollHint;
}

export function useFrameViewport(): FrameViewport {
  return React.useSyncExternalStore(subscribeFrameViewport, getFrameViewport, getFrameViewport);
}

/** Test-only: forget host state and observer data. */
export function resetFrameViewportForTests(): void {
  hostBand = null;
  hostSeen = false;
  observedBand = null;
  snapshot = computeViewport();
}

// ---------------------------------------------------------------------------
// CSS variables consumed by the theme (see FRAMED_VIEWPORT_STYLES)

export const FRAMED_ATTRIBUTE = 'data-rd-framed';

export function applyFrameViewportCssVars(viewport: FrameViewport, root: HTMLElement = document.documentElement): void {
  if (!viewport.framed) {
    clearFrameViewportCssVars(root);
    return;
  }
  const band = dialogBand(viewport);
  root.setAttribute(FRAMED_ATTRIBUTE, '');
  root.style.setProperty('--rd-visible-top', `${Math.round(band.top)}px`);
  root.style.setProperty('--rd-visible-height', `${Math.round(band.height)}px`);
  root.style.setProperty('--rd-visible-bottom', `${Math.round(band.bottom)}px`);
}

export function clearFrameViewportCssVars(root: HTMLElement = document.documentElement): void {
  root.removeAttribute(FRAMED_ATTRIBUTE);
  root.style.removeProperty('--rd-visible-top');
  root.style.removeProperty('--rd-visible-height');
  root.style.removeProperty('--rd-visible-bottom');
}

/** Keeps the visible-band CSS variables on <html> current while mounted. */
export function useFrameViewportCssVars(): void {
  React.useEffect(() => {
    const apply = () => applyFrameViewportCssVars(getFrameViewport());
    const unsubscribe = subscribeFrameViewport(apply);
    apply();
    return () => {
      unsubscribe();
      clearFrameViewportCssVars();
    };
  }, []);
}

const framed = `html[${FRAMED_ATTRIBUTE}]`;

/**
 * Global overrides (MuiCssBaseline) that keep overlays inside the visible band of a
 * tall auto-height frame. The modal root and its backdrop stay fixed to the whole
 * frame, so the backdrop still covers everything; only the centring container,
 * snackbars and temporary drawers move into the band.
 */
export const FRAMED_VIEWPORT_STYLES = {
  [`${framed} .MuiDialog-container`]: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 'var(--rd-visible-top, 0px)',
    bottom: 'auto',
    height: 'var(--rd-visible-height, 100%)',
  },
  [`${framed} .MuiSnackbar-anchorOriginTopLeft, ${framed} .MuiSnackbar-anchorOriginTopCenter, ${framed} .MuiSnackbar-anchorOriginTopRight`]: {
    top: 'calc(var(--rd-visible-top, 0px) + 16px)',
  },
  [`${framed} .MuiSnackbar-anchorOriginBottomLeft, ${framed} .MuiSnackbar-anchorOriginBottomCenter, ${framed} .MuiSnackbar-anchorOriginBottomRight`]: {
    bottom: 'calc(var(--rd-visible-bottom, 0px) + 16px)',
  },
  [`${framed} .MuiDrawer-modal .MuiDrawer-paperAnchorLeft, ${framed} .MuiDrawer-modal .MuiDrawer-paperAnchorRight`]: {
    top: 'var(--rd-visible-top, 0px)',
    height: 'var(--rd-visible-height, 100%)',
  },
} as const;

// ---------------------------------------------------------------------------
// Scrolling

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * Scroll every scrollable ancestor (host page included) minimally so that the
 * range [viewportTop, viewportTop + span] of this document is visible. A short
 * throw-away marker stands in for tall targets: `block: 'nearest'` does nothing
 * for an element that already overflows both viewport edges.
 */
export function scrollRangeIntoView(viewportTop: number, span: number, options: { left?: number; behavior?: ScrollBehavior } = {}): void {
  const marker = document.createElement('div');
  marker.setAttribute('aria-hidden', 'true');
  const docTop = Math.round(viewportTop + (window.scrollY || 0));
  const docLeft = Math.round((options.left ?? 0) + (window.scrollX || 0));
  marker.style.cssText = `position:absolute;top:${docTop}px;left:${docLeft}px;width:1px;height:${Math.max(1, Math.round(span))}px;pointer-events:none;visibility:hidden`;
  document.documentElement.appendChild(marker);
  try {
    // Correct for an offset (positioned) containing block.
    const drift = viewportTop - marker.getBoundingClientRect().top;
    if (Math.abs(drift) >= 1) marker.style.top = `${Math.round(docTop + drift)}px`;
    marker.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: options.behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth') });
  } finally {
    marker.remove();
  }
}
